import React, { useState } from 'react';
import {
  Form, Button, Row, Col, Card, Table, Modal,
} from 'react-bootstrap';
import { RoutePairGeoEditor } from '../../../Components/PricingGeo';

const genId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `tmp-${Date.now()}-${Math.floor(Math.random() * 1e6)}`;
};

const defaultRoute = () => ({
  Id: genId(),
  name: '',
  origin: {
    address: '',
    latitude: '',
    longitude: '',
    match_radius_km: 2,
  },
  destination: {
    address: '',
    latitude: '',
    longitude: '',
    match_radius_km: 2,
  },
  bidirectional: true,
  category_prices: [],
  priority: 100,
});

const describeEndpoint = (ep) => {
  if (!ep) return 'Not set';
  if (ep.address) return ep.address;
  const lat = Number(ep.latitude);
  const lng = Number(ep.longitude);
  if (Number.isFinite(lat) && Number.isFinite(lng)) {
    return `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
  }
  return 'Not set';
};

function OperatorOnlyRoutesEditor({
  routes = [],
  vehicleCategories = [],
  onChange,
}) {
  // Only one map mounted at a time — inside the open sub-modal.
  const [editingIdx, setEditingIdx] = useState(null);

  const updateRoute = (idx, patch) => {
    const next = [...routes];
    next[idx] = { ...next[idx], ...patch };
    onChange(next);
  };

  const addRoute = () => onChange([...routes, defaultRoute()]);

  const removeRoute = (idx) => {
    const next = [...routes];
    next.splice(idx, 1);
    onChange(next);
  };

  const setCategoryPrice = (idx, categoryId, price) => {
    const route = routes[idx];
    const categoryPrices = [...(route.category_prices || [])];
    const cpIdx = categoryPrices.findIndex((cp) => cp.vehicle_category_id === categoryId);
    if (price === '' || price === null || price === undefined) {
      if (cpIdx >= 0) categoryPrices.splice(cpIdx, 1);
    } else if (cpIdx >= 0) {
      categoryPrices[cpIdx] = { vehicle_category_id: categoryId, price: Number(price) };
    } else {
      categoryPrices.push({ vehicle_category_id: categoryId, price: Number(price) });
    }
    updateRoute(idx, { category_prices: categoryPrices });
  };

  const activeCategories = vehicleCategories.filter((c) => c.active === 1);

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h5 className="mb-0">Operator-only routes</h5>
        <Button variant="outline-primary" size="sm" onClick={addRoute}>
          <i className="fa fa-plus me-1" />
          Add route
        </Button>
      </div>

      <p className="text-muted small">
        Private fixed-price routes for this operator. When a booking matches both origin and
        destination (within match radius), the configured price is used directly.
      </p>

      {routes.length === 0 && (
        <div className="alert alert-light border small mb-0">
          No operator-only routes configured.
        </div>
      )}

      {routes.map((route, idx) => {
        const pricesByCat = new Map(
          (route.category_prices || []).map((cp) => [cp.vehicle_category_id, cp.price]),
        );
        return (
          <Card key={route.Id} className="mb-3">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <strong>{route.name || `Route ${idx + 1}`}</strong>
              <Button variant="outline-danger" size="sm" onClick={() => removeRoute(idx)}>
                <i className="fa fa-trash" />
              </Button>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-2">
                    <Form.Label className="small">Name *</Form.Label>
                    <Form.Control
                      type="text"
                      size="sm"
                      value={route.name || ''}
                      onChange={(e) => updateRoute(idx, { name: e.target.value })}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-2">
                    <Form.Label className="small">Bidirectional?</Form.Label>
                    <Form.Select
                      size="sm"
                      value={route.bidirectional ? '1' : '0'}
                      onChange={(e) => updateRoute(idx, { bidirectional: e.target.value === '1' })}
                    >
                      <option value="1">Yes</option>
                      <option value="0">No</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-2">
                    <Form.Label className="small">Priority</Form.Label>
                    <Form.Control
                      type="number"
                      size="sm"
                      value={route.priority ?? 100}
                      onChange={(e) => updateRoute(idx, { priority: Number(e.target.value) })}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Label className="small text-success">
                    <i className="fa fa-map-marker-alt me-1" />
                    Origin
                  </Form.Label>
                  <div className="small text-muted mb-1">{describeEndpoint(route.origin)}</div>
                  <div className="small text-muted">
                    Match radius:
                    {' '}
                    {route.origin?.match_radius_km ?? 2}
                    {' km'}
                  </div>
                </Col>
                <Col md={6}>
                  <Form.Label className="small text-danger">
                    <i className="fa fa-map-marker-alt me-1" />
                    Destination
                  </Form.Label>
                  <div className="small text-muted mb-1">
                    {describeEndpoint(route.destination)}
                  </div>
                  <div className="small text-muted">
                    Match radius:
                    {' '}
                    {route.destination?.match_radius_km ?? 2}
                    {' km'}
                  </div>
                </Col>
              </Row>
              <div className="mb-2 mt-2">
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => setEditingIdx(idx)}
                >
                  <i className="fa fa-map me-1" />
                  Edit endpoints on map
                </Button>
              </div>

              <h6 className="mt-3">Prices by vehicle category</h6>
              {activeCategories.length === 0 ? (
                <div className="alert alert-warning small mb-0">
                  No active vehicle categories.
                </div>
              ) : (
                <Table size="sm" bordered className="mb-0">
                  <thead>
                    <tr>
                      <th>Vehicle category</th>
                      <th>Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeCategories.map((cat) => (
                      <tr key={cat.Id}>
                        <td>{cat.name}</td>
                        <td>
                          <Form.Control
                            type="number"
                            size="sm"
                            step="0.01"
                            min={0}
                            value={pricesByCat.get(cat.Id) ?? ''}
                            onChange={(e) => setCategoryPrice(idx, cat.Id, e.target.value)}
                            placeholder="Leave empty to skip"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        );
      })}

      <Modal
        show={editingIdx !== null}
        onHide={() => setEditingIdx(null)}
        size="xl"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {editingIdx !== null && (routes[editingIdx]?.name || `Route ${editingIdx + 1}`)}
            {' — Endpoints'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editingIdx !== null && (
            <RoutePairGeoEditor
              origin={routes[editingIdx]?.origin}
              destination={routes[editingIdx]?.destination}
              onOriginChange={(origin) => updateRoute(editingIdx, { origin })}
              onDestinationChange={(destination) => updateRoute(editingIdx, { destination })}
              active={editingIdx !== null}
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setEditingIdx(null)}>
            Done
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default OperatorOnlyRoutesEditor;
